export const THAI_SHORT_MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];

/** Map informal Thai month abbreviations to proper short format (e.g. ตค → ต.ค.) */
const THAI_MONTH_NORMALIZE: Record<string, string> = {
  ตค: 'ต.ค.',
  ธค: 'ธ.ค.',
  มค: 'ม.ค.',
  มีค: 'มี.ค.',
  เมย: 'เม.ย.',
  มิย: 'มิ.ย.',
  กค: 'ก.ค.',
  กย: 'ก.ย.',
  กพ: 'ก.พ.',
  พค: 'พ.ค.',
  สค: 'ส.ค.',
  พย: 'พ.ย.',
};

/**
 * Normalize Thai month abbreviations in a period label to proper short format.
 * e.g. "ตค.-ธค.2566" → "ต.ค.-ธ.ค.2566", "มค.-มีค.2567" → "ม.ค.-มี.ค.2567"
 */
export function normalizeThaiPeriodLabel(label: string): string {
  if (!label || typeof label !== 'string') return label;
  let out = label;
  for (const [abbrev, proper] of Object.entries(THAI_MONTH_NORMALIZE)) {
    // Match abbrev with optional trailing period/dot (ตค. or ตค)
    const re = new RegExp(abbrev + '\\.?', 'g');
    out = out.replace(re, proper);
  }
  return out;
}

const parseMonthKey = (monthKey: string): { year: number; monthIndex: number } => {
  const [yearText, monthText] = monthKey.split('-');
  const year = Number(yearText);
  const monthNumber = Number(monthText);
  if (!Number.isFinite(year) || !Number.isFinite(monthNumber)) {
    throw new Error(`Invalid monthKey: ${monthKey}`);
  }
  if (monthNumber < 1 || monthNumber > 12) {
    throw new Error(`Invalid monthKey month: ${monthKey}`);
  }
  return { year, monthIndex: monthNumber - 1 };
};

export const formatThaiMonthYYBuddhist = (monthKey: string): string => {
  const { year, monthIndex } = parseMonthKey(monthKey);
  const buddhistYear = year + 543;
  const buddhistYearShort = String(buddhistYear).slice(-2);
  return `${THAI_SHORT_MONTHS[monthIndex]} ${buddhistYearShort}`;
};

/** For Recharts ticks and other callers where the value may be non-string or not `YYYY-MM`. */
export function safeFormatThaiMonthYYBuddhist(value: unknown): string {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  try {
    return formatThaiMonthYYBuddhist(s);
  } catch {
    return s;
  }
}

export function formatFiscalQuarterShort(value: unknown): string {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';

  const quarterFirst = s.match(/^(?:ไตรมาส(?:ที่)?\s*)?Q?\s*([1-4])\s*(?:\/|-|\s+)\s*(?:FY\s*)?(\d{2,4})$/i);
  const yearFirst = s.match(/^(?:FY\s*)?(\d{2,4})\s*(?:\/|-|\s+)\s*Q?\s*([1-4])$/i);
  const match = quarterFirst
    ? { quarter: quarterFirst[1], year: quarterFirst[2] }
    : yearFirst
      ? { quarter: yearFirst[2], year: yearFirst[1] }
      : null;
  if (!match) return s;

  const { quarter, year } = match;
  if (!quarter || !year) return s;

  return `${quarter}/${year.slice(-2)}`;
}

export const getYearMonthsFromReportingMonth = (reportingMonth: string): string[] => {
  const { year } = parseMonthKey(reportingMonth);
  return Array.from({ length: 12 }, (_value, idx) => {
    const month = String(idx + 1).padStart(2, '0');
    return `${year}-${month}`;
  });
};

const BANGKOK_TZ = 'Asia/Bangkok';

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Calendar + clock parts in Asia/Bangkok for a given instant. */
function getBangkokCalendarParts(date: Date): {
  year: number;
  monthIndex: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: BANGKOK_TZ,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  return {
    year: pick('year'),
    monthIndex: pick('month') - 1,
    day: pick('day'),
    hour: pick('hour'),
    minute: pick('minute'),
    second: pick('second'),
  };
}

export const THAI_LONG_MONTHS = [
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
] as const;

/**
 * Full Thai calendar date in Buddhist era (wall clock in Bangkok), e.g. "16 มีนาคม 2569".
 */
export function formatThaiBuddhistDateLongBangkok(date: Date): string {
  const { year, monthIndex, day } = getBangkokCalendarParts(date);
  const monthName = THAI_LONG_MONTHS[monthIndex] ?? '';
  const buddhistYear = year + 543;
  return `${day} ${monthName} ${buddhistYear}`;
}

/** Time-of-day in Bangkok, 24h, Thai suffix "น." */
export function formatThaiClockBangkok(date: Date): string {
  const { hour, minute } = getBangkokCalendarParts(date);
  return `${pad2(hour)}:${pad2(minute)} น.`;
}

/** Stable day key in Bangkok (YYYY-MM-DD) for grouping lists. */
export function formatBangkokDayKey(date: Date): string {
  const { year, monthIndex, day } = getBangkokCalendarParts(date);
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}
